import { PlusIcon } from '@phosphor-icons/react';
import { Button, LoadingOverlay, Menu, SaveBar } from '@skupreme/shared/ui';
import { useMetadata } from '@contexts/org-metadata';
import { ToastMessage } from '@utility/toast';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addableMethods,
  hasErrors,
  isRemovableMethod,
  mergeSelections,
  newMethodRow,
  newRowId,
  type RegionSelection,
  type TemplateDraft,
  toDraft,
  toTemplatePayload,
  validateDraft,
} from '../../lib/shipping-template';
import { flagIcon } from '../country-options';
import { InternationalCard, SpeedsCard } from './ExtraCards';
import { InternationalShippingModal } from './InternationalShippingModal';
import { MethodCard } from './MethodCard';
import { RegionSelectModal } from './RegionSelectModal';
import { useShippingTemplate } from './use-shipping-template';

type OrgRegion = {
  _id: string;
  region_alias?: string;
  name?: string;
};

export type ShippingTemplateProvider = {
  _id?: string;
  marketplace_region_id?: { _id?: string };
};

/* The method a region pick is for, and the row it edits; none adds a row. */
type RegionTarget = { method: string; rowId?: string };

const EMPTY_DRAFT: TemplateDraft = {
  methods: [],
  speeds: [],
  international: [],
};
const NO_SELECTION: RegionSelection = {};

/** What each shipping speed costs and takes per region, and fees abroad. */
export function ShippingTemplateTab({
  provider,
  isUser,
}: {
  provider?: ShippingTemplateProvider;
  isUser: boolean;
}) {
  const { template, regions, marketplaces, loading, save } =
    useShippingTemplate(provider?._id ?? '');
  const orgRegions = (useMetadata() as { regions?: OrgRegion[] } | undefined)
    ?.regions;

  const [draft, setDraft] = useState<TemplateDraft>(EMPTY_DRAFT);
  const [isDirty, setIsDirty] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regionTarget, setRegionTarget] = useState<RegionTarget | null>(null);
  const [addingInternational, setAddingInternational] = useState(false);

  const saved = useMemo(
    () => (loading ? null : toDraft(template, regions)),
    [loading, template, regions],
  );
  const reset = useCallback(() => {
    if (!saved) return;
    setDraft(saved);
    setIsDirty(false);
    setSubmitted(false);
  }, [saved]);

  useEffect(reset, [reset]);

  const update = (next: (current: TemplateDraft) => TemplateDraft) => {
    setDraft(next);
    setIsDirty(true);
  };

  const errors = useMemo(() => validateDraft(draft), [draft]);
  const shown = submitted ? errors : { methods: {}, rows: {}, speeds: {} };

  const marketplaceOptions = useMemo(
    () =>
      marketplaces.map(marketplace => ({
        value: marketplace.marketplace_id,
        label: marketplace.alias,
        leading: flagIcon(marketplace.country_code),
      })),
    [marketplaces],
  );
  const homeRegionId = template?.marketplace_region_id?._id;
  const currency = template?.marketplace_region_id?.currency_code || 'USD';
  const internationalOptions = useMemo(
    () =>
      (orgRegions ?? [])
        .filter(region => region._id !== homeRegionId)
        .map(region => ({
          value: region._id,
          label: region.region_alias ?? region.name ?? region._id,
          leading: flagIcon(region.name),
        })),
    [orgRegions, homeRegionId],
  );
  const freeInternational = internationalOptions.filter(
    option => !draft.international.some(row => row.regionId === option.value),
  );

  /* A row may not cover a state another row of its method already does. */
  const { taken, initial } = useMemo(() => {
    const method = draft.methods.find(
      entry => entry.name === regionTarget?.method,
    );
    const rows = method?.rows ?? [];
    return {
      taken: mergeSelections(
        rows
          .filter(row => row.id !== regionTarget?.rowId)
          .map(row => row.regions),
      ),
      initial:
        rows.find(row => row.id === regionTarget?.rowId)?.regions ??
        NO_SELECTION,
    };
  }, [draft.methods, regionTarget]);

  const confirmRegions = (selection: RegionSelection) => {
    if (!regionTarget) return;
    const { method, rowId } = regionTarget;
    update(current => ({
      ...current,
      methods: current.methods.map(entry =>
        entry.name !== method
          ? entry
          : {
              ...entry,
              rows: rowId
                ? entry.rows.map(row =>
                    row.id === rowId ? { ...row, regions: selection } : row,
                  )
                : [
                    ...entry.rows,
                    newMethodRow(
                      method,
                      selection,
                      marketplaces.map(
                        marketplace => marketplace.marketplace_id,
                      ),
                    ),
                  ],
            },
      ),
    }));
    setRegionTarget(null);
  };

  const editRows = (
    method: string,
    change: (
      rows: TemplateDraft['methods'][number]['rows'],
    ) => TemplateDraft['methods'][number]['rows'],
  ) =>
    update(current => ({
      ...current,
      methods: current.methods.map(entry =>
        entry.name === method ? { ...entry, rows: change(entry.rows) } : entry,
      ),
    }));

  const submit = async () => {
    setSubmitted(true);
    if (hasErrors(errors) || !provider?._id) return;
    try {
      setSaving(true);
      await save(
        toTemplatePayload(draft, regions, provider.marketplace_region_id?._id),
      );
      ToastMessage.success('Shipping Template saved successfully.');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingOverlay label="Loading" />;

  const addable = addableMethods(draft.methods);

  return (
    /* Room for the unsaved-changes bar, as on the Inventory tab. */
    <div className="sk:flex sk:flex-col sk:gap-4 sk:pb-16">
      {draft.methods.map(method => (
        <MethodCard
          key={method.name}
          method={method}
          tree={regions}
          marketplaceOptions={marketplaceOptions}
          error={shown.methods[method.name]}
          rowErrors={shown.rows}
          isDisabled={isUser}
          onAddRow={() => setRegionTarget({ method: method.name })}
          onEditRegions={rowId =>
            setRegionTarget({ method: method.name, rowId })
          }
          onDeleteRow={rowId =>
            editRows(method.name, rows => rows.filter(row => row.id !== rowId))
          }
          onChangeRow={(rowId, patch) =>
            editRows(method.name, rows =>
              rows.map(row => (row.id === rowId ? { ...row, ...patch } : row)),
            )
          }
          onRemove={
            isRemovableMethod(method.name)
              ? () =>
                  update(current => ({
                    ...current,
                    methods: current.methods.filter(
                      entry => entry.name !== method.name,
                    ),
                  }))
              : undefined
          }
        />
      ))}

      {addable.length > 0 ? (
        <div>
          <Menu
            aria-label="Add shipping speed"
            trigger={
              <Button variant="pill" isDisabled={isUser}>
                <PlusIcon size={14} aria-hidden />
                Shipping speed
              </Button>
            }
            actions={addable.map(name => ({ id: name, label: name }))}
            onAction={name =>
              update(current => ({
                ...current,
                methods: [...current.methods, { name, rows: [] }],
              }))
            }
          />
        </div>
      ) : null}

      <SpeedsCard
        speeds={draft.speeds}
        marketplaceOptions={marketplaceOptions}
        errors={shown.speeds}
        isDisabled={isUser}
        onAdd={() =>
          update(current => ({
            ...current,
            speeds: [
              ...current.speeds,
              { id: newRowId(), marketplaceId: '', speed: '' },
            ],
          }))
        }
        onChange={(id, patch) =>
          update(current => ({
            ...current,
            speeds: current.speeds.map(speed =>
              speed.id === id ? { ...speed, ...patch } : speed,
            ),
          }))
        }
        onDelete={id =>
          update(current => ({
            ...current,
            speeds: current.speeds.filter(speed => speed.id !== id),
          }))
        }
      />

      <InternationalCard
        rows={draft.international}
        regionOptions={internationalOptions}
        currency={currency}
        isDisabled={isUser}
        onAdd={() => setAddingInternational(true)}
        onChange={(id, patch) =>
          update(current => ({
            ...current,
            international: current.international.map(row =>
              row.id === id ? { ...row, ...patch } : row,
            ),
          }))
        }
        onDelete={id =>
          update(current => ({
            ...current,
            international: current.international.filter(row => row.id !== id),
          }))
        }
      />

      {isDirty ? (
        <SaveBar
          onDiscard={reset}
          onSave={submit}
          isDisabled={isUser}
          isSaving={saving}
        />
      ) : null}

      <RegionSelectModal
        isOpen={!!regionTarget}
        tree={regions}
        taken={taken}
        initial={initial}
        onClose={() => setRegionTarget(null)}
        onConfirm={confirmRegions}
      />
      <InternationalShippingModal
        isOpen={addingInternational}
        regionOptions={freeInternational}
        currency={currency}
        onClose={() => setAddingInternational(false)}
        onConfirm={(regionId, price) => {
          update(current => ({
            ...current,
            international: [
              ...current.international,
              { id: newRowId(), regionId, price: String(price) },
            ],
          }));
          setAddingInternational(false);
        }}
      />
    </div>
  );
}
